import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Search, Plus, User, Users, FileText, XCircle, ChevronDown, Eye } from "lucide-react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import {
    getProperties,
    getPipelineAgents,
    patchProperty,
    markPropertyListed,
    markPropertyLost,
} from "../../../api/agent";
import toast from "react-hot-toast";
import PipelinePropertyCard from "../components/PipelinePropertyCard";
import CategoryListPropertyView from "../components/CategoryListPropertyView";
import PipelineAppraisalEditModal from "../components/PipelineAppraisalEditModal";
import AppraisalTemperatureModal from "../components/AppraisalTemperatureModal";
import AddToDatabaseModal from "../components/AddToDatabaseModal";
import { hasAdminAccess } from "../../../utils/roleAccess";
import AgencyScopeSwitch, { useAgencyScope } from "../../../components/common/AgencyScopeSwitch";

// Map pipeline column id to property appraisal_temperature (backend: cold, warm, hot; unassigned = null/empty)
const COLUMN_TO_TEMPERATURE = {
    unassigned: null,
    low: "cold",
    medium: "warm",
    high: "hot",
};
const PipelinePageV2 = ({ title = "Off Market Pipeline", defaultType = "offmarket" }) => {
    const navigate = useNavigate();
    const { user } = useSelector((state) => state.auth);
    const isAdmin = hasAdminAccess(user);
    /*
     * WHOSE APPRAISALS THIS BOARD SHOWS.
     *
     * The pipeline can be a team-wide view (an admin managing the whole board,
     * Joshua Diokno, Monday 12709346275, 2026-08-07) or a personal one, and it
     * used to force team-wide on every admin. Simon Abbott, 31 Aug 2026: "can we
     * please completely separate my CRM from other agents? I think because I'm
     * admin I'm getting other agents appraisals in my pipeline."
     *
     * So it is now a CHOICE rather than a role side effect. The agency principal
     * and PAs still land on the team board; an admin lands on their own and can
     * flip the switch when they need to cover for someone. Agents never see the
     * switch — the BE has always scoped them to their own listings.
     */
    const {
        agencyWide: seesAgencyWide,
        setAgencyWide,
        canSwitch,
        canIncludeAgents,
    } = useAgencyScope("pipeline");
    /*
     * ONE AGENT AT A TIME.
     *
     * All agents / My appraisals is all-or-nothing, and reviewing the office
     * means reading it agent by agent. Michael Piccoli, 24 Sep 2026, Monday
     * 13091474717: "Similar to the database where you can switch between the
     * different agents, it would be good to be able to do the same in the
     * on-market and off-market pipeline's so we can see what the agents have on
     * an individual basis too (drop down with all the agent names to switch
     * between them all)."
     *
     * So this mirrors the Prospecting Database's switcher, deliberately, down
     * to the counts beside each name and the View only badge: a colleague's
     * board REPLACES your own rather than being folded in beside them, and it
     * is read-only, because "see what the agents have" is not permission to
     * drag another agent's appraisal from Warm to Cold.
     *
     * Not remembered between visits, again like the Database: the board you
     * land on is always your own, so the sidebar badge (which counts YOUR
     * untriaged appraisals) can never end up describing somebody else's board.
     */
    const [agents, setAgents] = useState([]);
    const [viewingAgent, setViewingAgent] = useState(null); // { id, name }
    const [switcherOpen, setSwitcherOpen] = useState(false);
    const agentScope = viewingAgent?.id;
    const isColleagueBoard = Boolean(agentScope);
    // Everything that writes comes off the page while a colleague's board is
    // open. The card omits a button whenever its handler prop is absent, which
    // is the same mechanism the view-only Lost column already uses.
    const canWrite = !isColleagueBoard;
    const [searchParams] = useSearchParams();
    const [propertiesList, setPropertiesList] = useState([]);
    // Appraisals that were marked Lost - fetched separately (they're withdrawn,
    // so the normal pipeline call excludes them) and shown in their own "Lost"
    // column so they can still be found and tracked (Michael Piccoli, Monday
    // 13090956131).
    const [lostList, setLostList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    // Narrow the board to one primary agent. George Bottas, CRM-2026-00097:
    // George, Paul & Spiros are a team and are co-agents on each other's
    // appraisals, so the board mixes all three; this lets each of them see one
    // agent's appraisals ("who to call themselves") without losing the shared
    // view. Purely client-side over what is already loaded, and separate from
    // the admin-only colleague switcher above. null = all agents.
    const [agentFilter, setAgentFilter] = useState(null); // primary agent id, or null
    const [agentFilterOpen, setAgentFilterOpen] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const [view, setView] = useState("board");
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [editingProperty, setEditingProperty] = useState(null);
    const [savingEdit, setSavingEdit] = useState(false);
    // The appraisal whose Cold / Warm / Hot is being changed from a row or card.
    const [movingProperty, setMovingProperty] = useState(null);
    const [savingMove, setSavingMove] = useState(false);
    /*
     * The appraisal being filed back into the Database. "I can add a property as
     * an appraisal but you cannot add your appraisal back to the database.
     * Please a selection or button to move a property of an appraisal back to
     * our database and select the correct tab" (Paul Jimoulis, CRM-2026-00070,
     * Monday 13057749471). The appraisal STAYS on the board: this puts the
     * vendor back on a calling list, it does not retire the appraisal. Taking it
     * off the board is what Withdraw is for.
     */
    const [databaseProperty, setDatabaseProperty] = useState(null);

    const appraisalPipelineType = defaultType === "onmarket" ? "on_market" : "off_market";

    const fetchProperties = useCallback(async () => {
        try {
            setLoading(true);
            const params = {
                page_size: 100,
                search: searchTerm || undefined,
                appraisal_pipeline: appraisalPipelineType,
                // A named agent is the whole scope, so the office flag goes with
                // it - sending both would read as "everyone, and also this one".
                agency_wide: !agentScope && seesAgencyWide ? true : undefined,
                agent: agentScope || undefined,
            };
            // Active board + the Lost column in one pass. Lost is a separate call
            // because withdrawn appraisals are excluded from the normal response.
            const [response, lostResponse] = await Promise.all([
                getProperties(params),
                getProperties({ ...params, lost: true }),
            ]);
            const results = response.data?.results ?? response.data ?? [];
            const list = Array.isArray(results) ? results : [];
            setPropertiesList(list);
            setTotalCount(response.data?.count ?? list.length);
            const lostResults = lostResponse.data?.results ?? lostResponse.data ?? [];
            setLostList(Array.isArray(lostResults) ? lostResults : []);
        } catch (error) {
            console.error("Error fetching appraisal properties:", error);
            toast.error("Failed to load appraisal listings");
            setPropertiesList([]);
            setLostList([]);
            setTotalCount(0);
        } finally {
            setLoading(false);
        }
    }, [searchTerm, appraisalPipelineType, seesAgencyWide, agentScope]);

    useEffect(() => {
        setPropertiesList([]);
        setLostList([]);
        setTotalCount(0);
        fetchProperties();
    }, [fetchProperties]);

    // Who is on the switcher, with a count per name so an empty board is
    // obvious before it is opened. Reloaded per board because the counts are
    // this board's, not the office's total.
    useEffect(() => {
        if (!canIncludeAgents) {
            setAgents([]);
            return;
        }
        let live = true;
        getPipelineAgents(appraisalPipelineType)
            .then((res) => {
                if (live) setAgents(res.data?.agents || []);
            })
            .catch(() => {
                // The switcher is additive — never hold the board up for it.
                if (live) setAgents([]);
            });
        return () => {
            live = false;
        };
    }, [canIncludeAgents, appraisalPipelineType]);

    useEffect(() => {
        setView("board");
        setSelectedCategory(null);
        // Switching boards lands you back on your own, so the header can never
        // say "Off Market" while the rows are somebody's On Market appraisals.
        setViewingAgent(null);
    }, [defaultType]);

    // Close the switcher when clicking elsewhere.
    useEffect(() => {
        if (!switcherOpen) return;
        const close = () => setSwitcherOpen(false);
        document.addEventListener("click", close);
        return () => document.removeEventListener("click", close);
    }, [switcherOpen]);

    // Same for the agent filter dropdown (its own button stops propagation).
    useEffect(() => {
        if (!agentFilterOpen) return;
        const close = () => setAgentFilterOpen(false);
        document.addEventListener("click", close);
        return () => document.removeEventListener("click", close);
    }, [agentFilterOpen]);

    const columns = [
        { id: "unassigned", title: "New / Unassigned", color: "gray", icon: <User className="w-4 h-4" /> },
        // "Cold, warm & hot leads need to change to Cold Appraisals, Warm
        // Appraisals, Hot Appraisals. This is where all appraisals are stored"
        // (Christian Gravias, Monday 12817261831, 17 Aug 2026). Raw seller leads
        // now live on the Seller Leads page instead.
        { id: "low", title: "Cold Appraisals", color: "blue", icon: <FileText className="w-4 h-4" /> },
        { id: "medium", title: "Warm Appraisals", color: "orange", icon: <FileText className="w-4 h-4" /> },
        { id: "high", title: "Hot Appraisals", color: "green", icon: <FileText className="w-4 h-4" /> },
        // Appraisals marked Lost live here so they can still be found and tracked
        // (Michael Piccoli, Monday 13090956131). View-only - no board actions.
        { id: "lost", title: "Lost Appraisals", color: "red", icon: <XCircle className="w-4 h-4" /> },
    ];

    // All columns are now visible to both admins and agents. 
    // Backend filters ensure agents only see what they are assigned to.
    const visibleColumns = columns;

    useEffect(() => {
        const priorityParam = searchParams.get("priority");
        if (priorityParam && (priorityParam !== "unassigned" || isAdmin)) {
            const column = visibleColumns.find((c) => c.id === priorityParam);
            if (column) {
                setSelectedCategory(column);
                setView("list");
            }
        }
    }, [searchParams, propertiesList.length, isAdmin]);

    // The primary agents present across the loaded board (both the active
    // columns and the Lost column), each with how many appraisals they lead.
    // Drives the agent filter dropdown; derived from the cards themselves so it
    // always lists exactly who is on this board, with no extra request.
    const agentOptions = useMemo(() => {
        const byId = new Map();
        [...propertiesList, ...lostList].forEach((p) => {
            const a = p.agent;
            if (!a || a.id == null) return;
            const entry = byId.get(a.id) || { id: a.id, name: a.name || "—", count: 0 };
            entry.count += 1;
            byId.set(a.id, entry);
        });
        return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
    }, [propertiesList, lostList]);

    // A selected agent who is no longer on the board (board reloaded/switched)
    // falls back to All so the user never sees an empty board for a stale pick.
    const activeAgentFilter =
        agentFilter != null && agentOptions.some((a) => a.id === agentFilter) ? agentFilter : null;

    const matchesAgentFilter = (p) =>
        activeAgentFilter == null || p.agent?.id === activeAgentFilter;

    const getPropertiesForColumn = (columnId) => {
        if (columnId === "lost") {
            return lostList.filter(matchesAgentFilter);
        }
        if (columnId === "unassigned") {
            return propertiesList.filter(
                (p) =>
                    matchesAgentFilter(p) &&
                    (!p.appraisal_temperature || String(p.appraisal_temperature).trim() === "")
            );
        }
        const temp = COLUMN_TO_TEMPERATURE[columnId];
        return propertiesList.filter(
            (p) =>
                matchesAgentFilter(p) &&
                p.appraisal_temperature &&
                p.appraisal_temperature.toLowerCase() === temp
        );
    };

    // The Lost column is view-only - a record of what was lost, not a board to
    // act on - so it gets no edit / temperature / Listed / Lost handlers (the
    // card omits a button whenever its handler prop is absent).
    const columnCardProps = (columnId) =>
        columnId === "lost" || !canWrite
            ? { onClick: handlePropertyClick }
            : {
                  onClick: handlePropertyClick,
                  onEdit: handleEditProperty,
                  onChangeTemperature: handleChangeTemperature,
                  onAddToDatabase: handleAddToDatabase,
                  onListed: handleMarkListed,
                  onLost: handleMarkLost,
              };

    const calculateColumnTotal = (items) => {
        return items.reduce((sum, p) => {
            const price = parseFloat(p.price) || 0;
            const rate = p.commission_rate != null ? parseFloat(p.commission_rate) : null;
            const amount = p.commission_amount != null ? parseFloat(p.commission_amount) : null;
            const commission = amount != null && amount > 0 ? amount : (rate != null && rate > 0 && price > 0 ? (rate / 100) * price : 0);
            return sum + commission;
        }, 0);
    };

    // The first row of the switcher: whatever "not a colleague's board" means
    // for this user. For the principal with the office view on, going back is
    // going back to All agents, not to their own handful of appraisals.
    const ownBoardLabel = canSwitch && seesAgencyWide ? "All agents" : "My appraisals";

    // Open a colleague's board, or `null` to come back to your own. The list
    // view is a slice of the board that was open, so it closes with it.
    const openBoard = (agent) => {
        setViewingAgent(agent ? { id: agent.id, name: agent.name } : null);
        setSwitcherOpen(false);
        setView("board");
        setSelectedCategory(null);
    };

    const handleCardClick = (category) => {
        setSelectedCategory(category);
        setView("list");
    };

    const handlePropertyClick = (property) => {
        if (property?.id) navigate(`/properties/${property.id}`);
    };

    // Straight to the form, no "Choose Listing Type" pop-up in the way: this
    // button says Add Appraisal, so the answer is always Appraisal (Simon
    // Abbott, 3 Sep 2026). The handoff state is the same one the Database's
    // "Convert to appraisal" already uses to skip that step.
    const handleAddAppraisal = () => {
        navigate("/properties/add-property", { state: { listingType: "appraisal" } });
    };

    const handleEditProperty = (property) => {
        setEditingProperty(property);
    };

    const handleChangeTemperature = (property) => {
        setMovingProperty(property);
    };

    /*
     * Move an appraisal between Cold / Warm / Hot with the reason recorded.
     *
     * Simon Abbott, Monday 12985995388, 7 Sep 2026: "We need to be able to change
     * a warm lead to hot or cold in list view. Add a button to change it so it
     * moves to hot or cold list and a note box to add a note as to why you
     * changed it."
     *
     * One PUT does both: appraisal_temperature moves it and appraisal_note is
     * saved as a PropertyNote, which is what the row then shows as its latest
     * note. No refetch is needed — the row leaves this list the moment the
     * temperature changes, because the list is filtered by it.
     */
    const handleSaveTemperature = async (property, temperature, note) => {
        const propertyId = property?.id;
        if (!propertyId) return;
        const previousTemperature = property.appraisal_temperature;
        try {
            setSavingMove(true);
            await patchProperty(propertyId, {
                appraisal_temperature: temperature,
                appraisal_note: note,
            });
            setPropertiesList((prev) =>
                prev.map((p) =>
                    p.id === propertyId ? { ...p, appraisal_temperature: temperature } : p
                )
            );
            const columnTitle =
                columns.find((c) => COLUMN_TO_TEMPERATURE[c.id] === temperature)?.title ||
                temperature;
            toast.success(`Moved to ${columnTitle}`);
            setMovingProperty(null);
            // Pick up the new latest note (and anything else the save recomputed).
            fetchProperties();
        } catch (error) {
            console.error("Error moving appraisal:", error);
            toast.error("Failed to move the appraisal");
            setPropertiesList((prev) =>
                prev.map((p) =>
                    p.id === propertyId ? { ...p, appraisal_temperature: previousTemperature } : p
                )
            );
        } finally {
            setSavingMove(false);
        }
    };

    const handleAddToDatabase = useCallback((property) => setDatabaseProperty(property), []);

    // "Listed" — promote the appraisal to a draft listing. It drops off this
    // board (no longer an Appraisal) and lands in My Listings for admin upload.
    const handleMarkListed = useCallback(async (property) => {
        const propertyId = property?.id;
        if (!propertyId) return;
        try {
            await markPropertyListed(propertyId);
            setPropertiesList((prev) => prev.filter((p) => p.id !== propertyId));
            setEditingProperty(null);
            toast.success("Moved to My Listings as a draft");
        } catch (error) {
            toast.error(error?.response?.data?.detail || "Failed to mark as listed");
        }
    }, []);

    // "Lost" — same outcome as the holding-area Lost: withdraw the card off the
    // board and mark the linked seller lead(s) lost with the chosen reason.
    const handleMarkLost = useCallback(async (property, reason, note) => {
        const propertyId = property?.id;
        if (!propertyId || !reason) return;
        try {
            await markPropertyLost(propertyId, reason, note);
            setPropertiesList((prev) => prev.filter((p) => p.id !== propertyId));
            setEditingProperty(null);
            toast.success("Marked as lost");
        } catch (error) {
            toast.error(error?.response?.data?.detail || "Failed to mark as lost");
        }
    }, []);

    const handleSaveEdit = async (propertyId, payload) => {
        try {
            setSavingEdit(true);
            const response = await patchProperty(propertyId, payload);
            const updated = response?.data ?? null;
            // Merge the server response back into the list so the card reflects
            // the fix immediately (falls back to the raw payload if none returned).
            setPropertiesList((prev) =>
                prev.map((p) => (p.id === propertyId ? { ...p, ...(updated || payload) } : p))
            );
            // When the edit moved it to the other pipeline the card is about to
            // disappear off this board, so say where it went rather than leaving
            // the agent to wonder (Simon Abbott, CRM-2026-00075).
            const movedTo = payload.appraisal_market_type;
            if (movedTo && movedTo !== appraisalPipelineType) {
                toast.success(
                    `Moved to the ${movedTo === "on_market" ? "On Market" : "Off Market"} Pipeline`
                );
            } else {
                toast.success("Appraisal updated");
            }
            setEditingProperty(null);
            // Refresh from the server to pick up recomputed fields (e.g. yields).
            fetchProperties();
        } catch (error) {
            console.error("Error updating appraisal:", error);
            toast.error("Failed to update appraisal");
        } finally {
            setSavingEdit(false);
        }
    };

    const colorClasses = {
        gray: {
            headerBg: "bg-[#FBFBFB]",
            badgeBg: "bg-[#E0E0E0]",
            badgeText: "text-[#6B7280]",
            text: "text-[#353B48]",
            cardBg: "bg-[#F9FAFB]",
            commBg: "bg-[#F3F4F6]",
            categoryTitle: "Unassigned Leads",
            ribbonBg: "bg-[#BDBDBD]",
            border: "border-[#E4E6E9]",
        },
        blue: {
            headerBg: "bg-[#DCF4FF]",
            badgeBg: "bg-[#50C8FF]",
            badgeText: "text-white",
            text: "text-[#0EA5E9]",
            cardBg: "bg-white",
            commBg: "bg-[#E0F2FE]",
            categoryTitle: "Cold Leads",
            ribbonBg: "bg-[#50C8FF]",
            border: "border-[#50C8FF]",
        },
        orange: {
            headerBg: "bg-[#FFEEE1]",
            badgeBg: "bg-[#F89542]",
            badgeText: "text-white",
            text: "text-[#F97316]",
            cardBg: "bg-white",
            commBg: "bg-[#FFF4E5]",
            categoryTitle: "Warm Leads",
            ribbonBg: "bg-[#F89542]",
            border: "border-[#F89542]",
        },
        green: {
            headerBg: "bg-[#EBFFF2]",
            badgeBg: "bg-[#4BCF0E]",
            badgeText: "text-white",
            text: "text-[#22C55E]",
            cardBg: "bg-white",
            commBg: "bg-[#EBFDF2]",
            categoryTitle: "Hot Leads",
            ribbonBg: "bg-[#4BCF0E]",
            border: "border-[#4BCF0E]",
        },
        red: {
            headerBg: "bg-[#FDECEC]",
            badgeBg: "bg-[#E5484D]",
            badgeText: "text-white",
            text: "text-[#DC2626]",
            cardBg: "bg-white",
            commBg: "bg-[#FDECEC]",
            categoryTitle: "Lost Appraisals",
            ribbonBg: "bg-[#E5484D]",
            border: "border-[#E5484D]",
        },
    };

    const onDragEnd = async (result) => {
        // A colleague's board is a read of it. The cards are not draggable
        // either, so this is the belt to that braces.
        if (!canWrite) return;
        const { destination, source, draggableId } = result;
        if (!destination) return;
        if (destination.droppableId === source.droppableId && destination.index === source.index) return;

        const propertyId = parseInt(draggableId, 10);
        const newColumnId = destination.droppableId;
        const newTemperature = COLUMN_TO_TEMPERATURE[newColumnId];

        const property = propertiesList.find((p) => p.id === propertyId);
        if (!property) return;

        const prevTemperature = property.appraisal_temperature;
        setPropertiesList((prev) =>
            prev.map((p) => (p.id === propertyId ? { ...p, appraisal_temperature: newTemperature ?? "" } : p))
        );

        try {
            await patchProperty(propertyId, { appraisal_temperature: newTemperature ?? "" });
            toast.success("Appraisal moved");
        } catch (error) {
            console.error("Error updating appraisal temperature:", error);
            toast.error("Failed to update");
            setPropertiesList((prev) =>
                prev.map((p) => (p.id === propertyId ? { ...p, appraisal_temperature: prevTemperature } : p))
            );
        }
    };

    if (view === "list" && selectedCategory) {
        const columnProperties = getPropertiesForColumn(selectedCategory.id);
        // The Lost list is view-only, like its board column — and so is every
        // list on a colleague's board.
        const lostView = selectedCategory.id === "lost" || !canWrite;
        return (
            <>
                <CategoryListPropertyView
                    title={title}
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    onBack={() => setView("board")}
                    onPropertyClick={handlePropertyClick}
                    onAddAppraisal={canWrite ? handleAddAppraisal : undefined}
                    onEditProperty={lostView ? undefined : handleEditProperty}
                    onChangeTemperature={lostView ? undefined : handleChangeTemperature}
                    onAddToDatabase={lostView ? undefined : handleAddToDatabase}
                    onListed={lostView ? undefined : handleMarkListed}
                    onLost={lostView ? undefined : handleMarkLost}
                    theme={colorClasses[selectedCategory.color]}
                    properties={columnProperties}
                />
                {/* Same quick-edit (and note) the board cards get, so an agent
                    reading the list can add a note without leaving it. */}
                <PipelineAppraisalEditModal
                    isOpen={Boolean(editingProperty)}
                    property={editingProperty}
                    onClose={() => setEditingProperty(null)}
                    onSave={handleSaveEdit}
                    onAddToDatabase={handleAddToDatabase}
                    onListed={handleMarkListed}
                    onLost={handleMarkLost}
                    saving={savingEdit}
                />
                <AppraisalTemperatureModal
                    isOpen={Boolean(movingProperty)}
                    property={movingProperty}
                    onClose={() => setMovingProperty(null)}
                    onSave={handleSaveTemperature}
                    saving={savingMove}
                />
                <AddToDatabaseModal
                    isOpen={Boolean(databaseProperty)}
                    property={databaseProperty}
                    onClose={() => setDatabaseProperty(null)}
                    onSaved={() => setDatabaseProperty(null)}
                />
            </>
        );
    }

    return (
        <div className="min-h-screen bg-[#FDFDFD] font-sans text-[#353B48]">
            <div className="p-3 sm:p-4 lg:p-2 animate-fade-in">
                <div className="flex flex-col md:flex-row md:items-center justify-between mb-4 sm:mb-6 gap-3 sm:gap-4">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <h1 className="text-xl sm:text-2xl md:text-[28px] font-bold tracking-tight text-[#353B48]">
                                {isColleagueBoard ? `${viewingAgent.name}'s ${title}` : title}
                            </h1>
                            {/* The agent switcher, mirroring the Prospecting
                                Database's (Michael Piccoli, Monday 13091474717).
                                Admin tier only, and only once there is somebody
                                to switch to — an agent has always had just their
                                own board and gets no dropdown at all. */}
                            {canIncludeAgents && agents.length > 0 && (
                                <div className="relative" onClick={(e) => e.stopPropagation()}>
                                    <button
                                        type="button"
                                        onClick={() => setSwitcherOpen((o) => !o)}
                                        /* The principal's All agents / My appraisals
                                           toggle carries the same two words, so name
                                           this one for anybody - or anything - that
                                           has to tell the two apart. */
                                        title="Switch between agents"
                                        aria-haspopup="true"
                                        aria-expanded={switcherOpen}
                                        className="px-3 py-1.5 rounded-lg border border-[#E6E7E9] bg-white text-xs font-bold text-[#353B48] flex items-center gap-1.5"
                                    >
                                        <Users size={13} className="text-[#55C5D0]" />
                                        {isColleagueBoard ? viewingAgent.name : ownBoardLabel}
                                        <ChevronDown size={13} className="text-[#9CA3AF]" />
                                    </button>
                                    {switcherOpen && (
                                        <div className="absolute left-0 mt-1 w-72 bg-white border border-[#E6E7E9] rounded-xl shadow-xl z-40 py-1 max-h-80 overflow-y-auto custom-scrollbar">
                                            <button
                                                type="button"
                                                onClick={() => openBoard(null)}
                                                className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
                                                    isColleagueBoard ? "text-[#353B48]" : "font-bold text-[#0e7d86]"
                                                }`}
                                            >
                                                {ownBoardLabel}
                                            </button>
                                            <div className="border-t border-gray-100 my-1" />
                                            <p className="px-3 py-1 text-[10px] uppercase tracking-wide text-[#9CA3AF] font-bold">
                                                Every agent · view only
                                            </p>
                                            {agents.map((agent) => (
                                                <button
                                                    key={agent.id}
                                                    type="button"
                                                    onClick={() => openBoard(agent)}
                                                    className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center justify-between gap-2 ${
                                                        String(agentScope) === String(agent.id)
                                                            ? "font-bold text-[#0e7d86]"
                                                            : "text-[#353B48]"
                                                    }`}
                                                >
                                                    <span className="truncate">{agent.name}</span>
                                                    <span className="text-[10px] text-[#9CA3AF] shrink-0">
                                                        {agent.count}
                                                    </span>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                            {isColleagueBoard && (
                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-gray-100 text-[#6B7280] text-[11px] font-bold">
                                    <Eye size={12} /> View only
                                </span>
                            )}
                        </div>
                        <p className="text-[#6B7280] text-xs sm:text-sm font-medium">
                            {isColleagueBoard
                                ? `${viewingAgent.name}'s appraisals, open to you for review. Read-only: you can look through this board but not change it.`
                                : canSwitch && seesAgencyWide
                                ? "Every agent's seller leads across the office"
                                : "Manage and track your seller leads"}
                        </p>
                    </div>
                    {/* All agents / My appraisals is about YOUR board, so it comes
                        off while somebody else's is open — the switcher above is
                        what puts it back. */}
                    {canSwitch && !isColleagueBoard && (
                        <AgencyScopeSwitch
                            agencyWide={seesAgencyWide}
                            onChange={setAgencyWide}
                            mineLabel="My appraisals"
                            agencyLabel="All agents"
                            className="md:ml-auto md:mr-3"
                        />
                    )}
                    {/* Adding an appraisal always adds it to YOUR board, so it
                        has no meaning while a colleague's is open. */}
                    {canWrite && (
                        <button
                            type="button"
                            className="bg-[#55C5D0] hover:bg-[#45b5c0] text-white px-4 sm:px-5 py-2 sm:py-2.5 rounded-lg font-bold flex items-center justify-center gap-2 transition-all shadow-md shadow-[#55C5D0]/20 text-xs sm:text-sm w-full sm:w-auto"
                            onClick={handleAddAppraisal}
                        >
                            <Plus className="w-3 h-3 sm:w-4 sm:h-4" />
                            Add Appraisal
                        </button>
                    )}
                </div>

                <div className="bg-white border border-[#E6E7E9] min-h-[60px] sm:h-[89px] flex flex-col sm:flex-row sm:items-center gap-3 px-4 sm:px-6 py-3 sm:py-0 mb-6 sm:mb-8 rounded-xl shadow-[0_2px_15px_-3px_rgba(0,0,0,0.07)]">
                    <div className="relative w-full">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] w-4 h-4" />
                        <input
                            type="text"
                            placeholder="Search leads/Property..."
                            className="w-full pl-10 pr-4 py-2.5 sm:py-3 bg-white border border-[#F3F4F6] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] transition-all text-xs sm:text-sm font-medium"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    {/* Agent filter: narrow the board to one primary agent's
                        appraisals (CRM-2026-00097). Shown once the board carries
                        more than one agent, so a solo board stays uncluttered. */}
                    {agentOptions.length > 1 && (
                        <div
                            className="relative w-full sm:w-auto flex-shrink-0"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <button
                                type="button"
                                onClick={() => setAgentFilterOpen((o) => !o)}
                                className="w-full sm:w-auto flex items-center justify-between gap-2 px-4 py-2.5 sm:py-3 bg-white border border-[#F3F4F6] rounded-lg hover:border-[#55C5D0] transition-all text-xs sm:text-sm font-medium text-[#353B48]"
                            >
                                <span className="flex items-center gap-2">
                                    <Users className="w-4 h-4 text-[#9CA3AF]" />
                                    {activeAgentFilter == null
                                        ? "All agents"
                                        : agentOptions.find((a) => a.id === activeAgentFilter)?.name}
                                </span>
                                <ChevronDown className="w-4 h-4 text-[#9CA3AF]" />
                            </button>
                            {agentFilterOpen && (
                                <div className="absolute right-0 z-20 mt-1 w-full sm:w-56 max-h-72 overflow-y-auto custom-scrollbar bg-white border border-[#E6E7E9] rounded-lg shadow-lg py-1">
                                    <button
                                        type="button"
                                        onClick={() => { setAgentFilter(null); setAgentFilterOpen(false); }}
                                        className={`w-full flex items-center justify-between px-4 py-2 text-xs sm:text-sm text-left hover:bg-[#F3F4F6] ${activeAgentFilter == null ? "font-bold text-[#55C5D0]" : "text-[#353B48]"}`}
                                    >
                                        <span>All agents</span>
                                        <span className="text-[#9CA3AF]">{propertiesList.length + lostList.length}</span>
                                    </button>
                                    {agentOptions.map((a) => (
                                        <button
                                            key={a.id}
                                            type="button"
                                            onClick={() => { setAgentFilter(a.id); setAgentFilterOpen(false); }}
                                            className={`w-full flex items-center justify-between px-4 py-2 text-xs sm:text-sm text-left hover:bg-[#F3F4F6] ${activeAgentFilter === a.id ? "font-bold text-[#55C5D0]" : "text-[#353B48]"}`}
                                        >
                                            <span className="truncate">{a.name}</span>
                                            <span className="text-[#9CA3AF] ml-2 flex-shrink-0">{a.count}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {loading ? (
                    <div className="flex items-center justify-center py-16 text-[#6B7280]">Loading...</div>
                ) : (
                    <DragDropContext onDragEnd={onDragEnd}>
                        <div className="block md:hidden space-y-4 pb-4">
                            {visibleColumns.map((column) => {
                                const columnItems = getPropertiesForColumn(column.id);
                                const totalValue = calculateColumnTotal(columnItems);
                                const theme = colorClasses[column.color];
                                return (
                                    <div
                                        key={column.id}
                                        className="w-full flex flex-col rounded-xl border-[0.85px] border-[#E4E6E9] bg-[#FBFBFB] overflow-hidden shadow-sm"
                                    >
                                        <div
                                            className={`p-3 flex items-center justify-between ${theme.headerBg} border-b-[0.85px] border-[#E4E6E9] cursor-pointer hover:opacity-80 transition-opacity`}
                                            onClick={() => handleCardClick(column)}
                                        >
                                            <div className="flex items-center gap-2 flex-1 min-w-0">
                                                <div className={`w-5 h-5 rounded-[7px] ${theme.badgeBg} flex items-center justify-center ${theme.badgeText} font-bold text-[10px] flex-shrink-0`}>
                                                    {columnItems.length || 0}
                                                </div>
                                                <h2 className="text-sm font-bold text-[#353B48] truncate">{column.title}</h2>
                                            </div>
                                            <div className="flex items-center gap-1 text-xs font-medium text-[#6B7280] leading-[100%] whitespace-nowrap ml-2">
                                                <span>$</span>
                                                <span>{totalValue.toLocaleString()}</span>
                                            </div>
                                        </div>
                                        <Droppable droppableId={column.id}>
                                            {(provided, snapshot) => (
                                                <div
                                                    {...provided.droppableProps}
                                                    ref={provided.innerRef}
                                                    className={`flex flex-col gap-2 p-2 max-h-[400px] overflow-y-auto custom-scrollbar transition-colors ${snapshot.isDraggingOver ? "bg-gray-100/50" : ""}`}
                                                >
                                                    {columnItems.length > 0 ? (
                                                        columnItems.map((property, index) => (
                                                            <Draggable
                                                                key={property.id}
                                                                draggableId={String(property.id)}
                                                                index={index}
                                                                isDragDisabled={!canWrite}
                                                            >
                                                                {(provided, snapshot) => (
                                                                    <div
                                                                        ref={provided.innerRef}
                                                                        {...provided.draggableProps}
                                                                        {...provided.dragHandleProps}
                                                                        className={snapshot.isDragging ? "z-50" : ""}
                                                                        onClick={(e) => { e.stopPropagation(); handlePropertyClick(property); }}
                                                                    >
                                                                        <PipelinePropertyCard property={property} theme={theme} {...columnCardProps(column.id)} />
                                                                    </div>
                                                                )}
                                                            </Draggable>
                                                        ))
                                                    ) : (
                                                        <div className="flex flex-col items-center justify-center py-8 text-[#9CA3AF] gap-3">
                                                            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center border border-[#F3F4F6] shadow-sm">
                                                                <User className="w-6 h-6 text-[#D1D5DB]" />
                                                            </div>
                                                            <p className="text-sm font-medium font-sans text-[#9CA3AF]">No leads</p>
                                                        </div>
                                                    )}
                                                    {provided.placeholder}
                                                </div>
                                            )}
                                        </Droppable>
                                    </div>
                                );
                            })}
                        </div>

                        <div className="hidden md:block overflow-x-auto pb-4 custom-scrollbar -mx-4 px-4">
                            <div className="flex gap-4 min-w-[1200px]">
                                {visibleColumns.map((column) => {
                                    const columnItems = getPropertiesForColumn(column.id);
                                    const totalValue = calculateColumnTotal(columnItems);
                                    const theme = colorClasses[column.color];
                                    return (
                                        <div
                                            key={column.id}
                                            className="flex-1 min-w-[300px] flex flex-col rounded-xl border-[0.85px] border-[#E4E6E9] bg-[#FBFBFB] overflow-hidden shadow-sm h-[702px]"
                                        >
                                            <div
                                                className={`p-4 flex items-center justify-between ${theme.headerBg} border-b-[0.85px] border-[#E4E6E9] cursor-pointer hover:opacity-80 transition-opacity`}
                                                onClick={() => handleCardClick(column)}
                                            >
                                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                                    <div className={`w-6 h-6 rounded-[7px] ${theme.badgeBg} flex items-center justify-center ${theme.badgeText} font-bold text-[11px] flex-shrink-0`}>
                                                        {columnItems.length || 0}
                                                    </div>
                                                    <h2 className="text-[14px] font-bold text-[#353B48] truncate">{column.title}</h2>
                                                </div>
                                                <div className="flex items-center gap-1 text-[12px] font-medium text-[#6B7280] leading-[100%] whitespace-nowrap ml-2">
                                                    <span>$</span>
                                                    <span>{totalValue.toLocaleString()}</span>
                                                </div>
                                            </div>
                                            <Droppable droppableId={column.id}>
                                                {(provided, snapshot) => (
                                                    <div
                                                        {...provided.droppableProps}
                                                        ref={provided.innerRef}
                                                        className={`flex-1 flex flex-col gap-3 p-3 overflow-y-auto custom-scrollbar transition-colors ${snapshot.isDraggingOver ? "bg-gray-100/50" : ""}`}
                                                    >
                                                        {columnItems.length > 0 ? (
                                                            columnItems.map((property, index) => (
                                                                <Draggable
                                                                    key={property.id}
                                                                    draggableId={String(property.id)}
                                                                    index={index}
                                                                    isDragDisabled={!canWrite}
                                                                >
                                                                    {(provided, snapshot) => (
                                                                        <div
                                                                            ref={provided.innerRef}
                                                                            {...provided.draggableProps}
                                                                            {...provided.dragHandleProps}
                                                                            className={snapshot.isDragging ? "z-50" : ""}
                                                                            onClick={(e) => { e.stopPropagation(); handlePropertyClick(property); }}
                                                                        >
                                                                            <PipelinePropertyCard property={property} theme={theme} {...columnCardProps(column.id)} />
                                                                        </div>
                                                                    )}
                                                                </Draggable>
                                                            ))
                                                        ) : (
                                                            <div className="flex-1 flex flex-col items-center justify-center text-[#9CA3AF] gap-3">
                                                                <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center border border-[#F3F4F6] shadow-sm">
                                                                    <User className="w-6 h-6 text-[#D1D5DB]" />
                                                                </div>
                                                                <p className="text-[14px] font-medium font-sans text-[#9CA3AF]">No leads</p>
                                                            </div>
                                                        )}
                                                        {provided.placeholder}
                                                    </div>
                                                )}
                                            </Droppable>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </DragDropContext>
                )}
            </div>

            <PipelineAppraisalEditModal
                isOpen={Boolean(editingProperty)}
                property={editingProperty}
                saving={savingEdit}
                onClose={() => setEditingProperty(null)}
                onSave={handleSaveEdit}
                onAddToDatabase={handleAddToDatabase}
                onListed={handleMarkListed}
                onLost={handleMarkLost}
            />
            <AppraisalTemperatureModal
                isOpen={Boolean(movingProperty)}
                property={movingProperty}
                saving={savingMove}
                onClose={() => setMovingProperty(null)}
                onSave={handleSaveTemperature}
            />
            <AddToDatabaseModal
                isOpen={Boolean(databaseProperty)}
                property={databaseProperty}
                onClose={() => setDatabaseProperty(null)}
                onSaved={() => setDatabaseProperty(null)}
            />
        </div>
    );
};

export default PipelinePageV2;
